// ==================================================
// IMPORTS: React & Core Libraries
// ==================================================

import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// ==================================================
// IMPORTS: Global Styles
// ==================================================

import './components/shared/ToastStyles.css';
import './styles/Theme.css';

// ==================================================
// IMPORTS: Application Providers
//
// Purpose:
// Provides global state and settings
// across the entire Kuddlecore application.
//
// Examples:
// Theme
// Authentication
// User Data
// Center Data
//
// ==================================================

import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { CompactModeProvider } from './context/CompactModeContext';
import { UserProvider } from './context/UserContext';
import { CenterProvider } from './context/CenterContext';

// ==================================================
// IMPORTS: Shared Components
// ==================================================

import StatusBanner from './components/shared/StatusBanner';
import ComposeMessage from './components/messages/ComposeMessage';
import Layout from './components/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { StatusBannerProvider } from './components/shared/StatusBannerContext';

// ==================================================
// IMPORTS: Core Pages
//
// Purpose:
// Main application pages available
// through navigation and routing.
//
// ==================================================

import KuddleBoard from './pages/KuddleBoard';
import Students from './pages/Students';
import Teachers from './pages/Teachers';
import Attendance from './pages/Attendance';
import Messages from './pages/Messages';
import Reports from './pages/Reports';
import SettingsPage from './pages/SettingsPage';
import SettingsFolders from './pages/SettingsFolders';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import ChatLogs from './pages/ChatLogs';
import NotFound from './pages/NotFound';
import RemindersDashboard from "./pages/RemindersDashboard";
import StudentProfile from './pages/StudentProfile';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import ForgotPassword from './pages/ForgotPassword';
import TwoFactorVerify from './pages/TwoFactorVerify';
import AcceptInvite from './pages/AcceptInvite';
import SecurityCenter from './pages/SecurityCenter';
import CenterCommand from "./pages/CenterCommand";
import TrustDevice from "./pages/TrustDevice";
import SessionAnalyticsPage from './pages/SessionAnalyticsPage';
import AddCenter from "./pages/AddCenter"; // adjust path if needed
import CentersSettings from "./pages/CentersSettings";
import NotificationPreferences from "./pages/NotificationPreferences";
import NotepadPage from "./pages/NotepadPage";
import CalendarPage from "./pages/CalendarPage";
import Trash from "./pages/Trash";

// ==================================================
// APP COMPONENT
//
// Purpose:
// Root component for Kuddlecore.
//
// Responsibilities:
// - Loads Providers
// - Creates Router
// - Registers Routes
// - Protects Secure Pages
// - Displays Global Notifications
//
// ==================================================
const App = () => {
  return (
    // ==================================================
    // GLOBAL PROVIDERS
    // ==================================================
    <ThemeProvider>
      <CompactModeProvider>
        <AuthProvider>
          <UserProvider>
            <CenterProvider>
              {/* APPLICATION ROUTER */}
              <Router>
                <StatusBannerProvider>
                  {/* ==================================================
    GLOBAL UI SERVICES

    Purpose:
    Services available everywhere.

    Examples:
    Status Banner
    Toast Notifications
    Analytics
================================================== */}
                  <StatusBanner />
                  <ToastContainer position="top-center" autoClose={3000} hideProgressBar />

                  {/* ==================================================
    PUBLIC ROUTES

    Purpose:
    Routes accessible before login.

    Examples:
    Login
    Forgot Password
    Reset Password
================================================== */}

                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/verify-2fa" element={<TwoFactorVerify />} />
                    <Route path="/accept-invite" element={<AcceptInvite />} />
                    <Route path="/trust-device" element={<TrustDevice />} />

                    {/* ==================================================
    SECURE STANDALONE ROUTES

    Purpose:
    Protected pages outside Layout.

    Examples:
    Security Center
    Command Center
================================================== */}

                    <Route
                      path="/security"
                      element={
                        <ProtectedRoute>
                          <SecurityCenter />
                        </ProtectedRoute>
                      }
                    />

                    {/* ==================================================
    MAIN APPLICATION ROUTES

    Purpose:
    Pages displayed inside the main
    Kuddlecore Layout and Sidebar.
================================================== */}

                    <Route
                      path="/command-center"
                      element={
                        <ProtectedRoute>
                          <CenterCommand />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <Layout />
                        </ProtectedRoute>
                      }
                    >
                      {/* ==================================================
    KUDDLEBOARD & CORE MODULES
================================================== */}
                      <Route index element={<KuddleBoard />} />
                      {/* ==================================================
    STUDENT MANAGEMENT
================================================== */}
                      <Route path="students" element={<Students />} />
                      <Route path="students/:id" element={<StudentProfile />} />
                      <Route path="teachers" element={<Teachers />} />
                      <Route path="attendance" element={<Attendance />} />
                      {/* ==================================================
    COMMUNICATIONS
================================================== */}
                      <Route path="messages" element={<Messages />} />
                      <Route path="messages/compose" element={<ComposeMessage />} />
                      <Route path="settings/folders" element={<SettingsFolders />} />
                      <Route path="profile" element={<Profile />} />
                      <Route path="notifications" element={<Notifications />} />

                      {/* ==================================================
    PRODUCTIVITY TOOLS
================================================== */}
                      <Route path="reminders" element={<RemindersDashboard />} />
                      <Route path="/calendar" element={<CalendarPage />} />
                      <Route path="/trash" element={<Trash />} />
                      <Route path="notepad" element={<NotepadPage />} />
                      <Route path="*" element={<NotFound />} />
                      <Route path="/centers/new" element={<AddCenter />} />
                      {/* ==================================================
    SETTINGS & ADMINISTRATION
================================================== */}
                      <Route path="settings" element={<SettingsPage />} />
                      <Route path="/settings/centers" element={<CentersSettings />} />
                      <Route path="/settings/notifications" element={<NotificationPreferences />} />
                      {/* ==================================================
    ANALYTICS & REPORTING
================================================== */}
                      <Route path="reports" element={<Reports />} />
                      <Route path="chatlogs" element={<ChatLogs />} />
                      <Route path="analytics/sessions" element={<SessionAnalyticsPage />} />
                    </Route>
                  </Routes>


                </StatusBannerProvider>
              </Router>
            </CenterProvider>
          </UserProvider>
        </AuthProvider>
      </CompactModeProvider>
    </ThemeProvider>
  );
};


export default App;
